import { PaymentOption, RevenueCustomerType, ServiceCategoryType } from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import type { NoddiBarChartStack } from 'noddi-ui-web';

export type RevenueColor = NoddiBarChartStack['color'];

/** How a breakdown row and its chart series look: the same color in both, so a row is findable in the chart. */
export interface RevenueStyle {
  icon: NoddiIconName;
  color: RevenueColor;
}

const SERVICE_TYPE_ICONS: Record<ServiceCategoryType, NoddiIconName> = {
  [ServiceCategoryType.WHEEL_CHANGE]: 'Wrench',
  [ServiceCategoryType.WHEEL_STORAGE]: 'Warehouse',
  [ServiceCategoryType.CAR_WASH]: 'Droplet',
  [ServiceCategoryType.TIRE_SALE]: 'ShoppingCart',
  [ServiceCategoryType.RIM_SALE]: 'Wheel',
  [ServiceCategoryType.WINDSCREEN_REPAIR]: 'Car',
  [ServiceCategoryType.EXTERNAL_IMPORT]: 'Download'
};

/** Lines without a service, such as delivery fees, and categories with no type yet. */
const OTHER_SERVICE_ICON: NoddiIconName = 'Package';

export const serviceIcon = (type: ServiceCategoryType | null | undefined): NoddiIconName =>
  type === null || type === undefined ? OTHER_SERVICE_ICON : SERVICE_TYPE_ICONS[type];

// Five chart colors: the biggest services each get one, the rest share the last.
const RANKED_SERVICE_COLORS = ['chart-1', 'chart-2', 'chart-3', 'chart-4'] as const;
export const OTHER_SERVICES_COLOR: RevenueColor = 'chart-5';

/** One color per service by revenue rank, shared by the chart and the service list. */
export const rankedServiceColors = (rankedIds: (number | null)[]) =>
  new Map<number | null, RevenueColor>(
    rankedIds.map((id, index) => [id, RANKED_SERVICE_COLORS[index] ?? OTHER_SERVICES_COLOR])
  );

export const CHARTED_SERVICES = RANKED_SERVICE_COLORS.length;

export const PAYMENT_OPTION_STYLES: Record<PaymentOption, RevenueStyle> = {
  [PaymentOption.PAY_BEFORE]: { icon: 'CreditCard', color: 'chart-1' },
  [PaymentOption.PAY_AFTER]: { icon: 'Clock', color: 'chart-2' },
  [PaymentOption.INVOICE]: { icon: 'FileText', color: 'chart-4' }
};

export const CUSTOMER_TYPE_STYLES: Record<RevenueCustomerType, RevenueStyle> = {
  [RevenueCustomerType.PRIVATE]: { icon: 'User', color: 'chart-2' },
  [RevenueCustomerType.BUSINESS]: { icon: 'Building2', color: 'chart-1' }
};
